import React, { useState, useEffect } from 'react'
import { Table, Button, Modal, Input, Space, Select, Switch } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import axios from 'axios'

import {
  BranchMaster,
  CreateRoomMaster,
  GetRoomMaster,
  UpdateRoomMaster,
  DeleteRoomMaster,
  UpdateRoomStatus,
  GetClassMaster,
} from '../../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'

const RoomMaster = () => {
  const storedBranchId = sessionStorage.getItem('selected_branch')

  const [data, setData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  // --- FORM STATES USING useState ---
  const [roomName, setRoomName] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [modalStatus, setModalStatus] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [isRoomBooked, setIsRoomBooked] = useState(false)
  const [classList, setClassList] = useState([])
  const [selectedClass, setSelectedClass] = useState(null)

  useEffect(() => {
    fetchClasses()
    fetchRooms()
  }, [])

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${GetClassMaster}/${storedBranchId}`)

      // Adjust keys based on your API response
      const formatted = res.data.map((item) => ({
        label: item.class_name,
        value: item.class_id,
      }))

      setClassList(formatted)
    } catch (err) {
      toast.error('Failed to load classes')
    }
  }

  const fetchRooms = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${GetRoomMaster}/${storedBranchId}`)

      const formatted = res.data.map((item, index) => ({
        key: item.room_id,
        index: index + 1,
        room_no: item.room_no,
        status: item.status === 1 ? 'Active' : 'Inactive',
        branch_id: item.branch_id,
        class_id: item.class_id,
        isBooked: item.roombooked && item.roombooked.length > 0,
        class: item.class,
      }))

      setData(formatted)
    } catch (err) {
      toast.error('Failed to load rooms')
    } finally {
      setLoading(false) // 👉 Stop loader
    }
  }

  // --- OPEN MODAL ---
  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingId(record.key)
      setRoomName(record.room_no)
      setModalStatus(record.status === 'Active')
      setIsRoomBooked(record.isBooked)
      setSelectedClass(record.class_id)
    } else {
      setEditingId(null)
      setRoomName('')
      setModalStatus(true)
      setIsRoomBooked(false)
      setSelectedClass(null)
    }
    setIsModalOpen(true)
  }

  // --- SAVE OR UPDATE ---
  const handleSave = async () => {
    if (!selectedClass) return toast.error('Please select class')
    if (!roomName.trim()) return toast.error('Please enter room name')

    const payload = {
      branch_id: storedBranchId,
      class_id: selectedClass,
      room_no: roomName,
    }

    try {
      if (editingId) {
        const res = await axios.put(`${UpdateRoomMaster}/${editingId}`, payload)
        if (res.data === 'UPDATED') {
          toast.success('Room updated successfully')
        }
      } else {
        const res = await axios.post(CreateRoomMaster, payload)
        if (res.data === 'SAVED') {
          toast.success('Room added successfully')
        }
      }

      fetchClasses()
      fetchRooms()
      setIsModalOpen(false)
      setEditingId(null)
      setSelectedClass('')
    } catch (err) {
      toast.error('Failed to save room')
    }
  }

  const handleModalStatusChange = async (checked) => {
    setModalStatus(checked)
    setUpdatingStatus(true)

    try {
      const payload = {
        room_id: editingId,
        status: checked ? 1 : 2,
      }

      await axios.put(UpdateRoomStatus, payload)
      toast.success('Status updated')

      // update UI table instantly
      setData((prev) =>
        prev.map((item) =>
          item.key === editingId
            ? { ...item, status: checked ? 'Active' : 'Inactive' }
            : item,
        ),
      )
    } catch (err) {
      toast.error('Failed to update status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  // --- SEARCH ---
  const filteredData = data.filter((item) =>
    ((item.room_no ?? '') + '')
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  )

  const columns = [
    { title: 'Sr No', dataIndex: 'index' },
    {
      title: 'Class',
      dataIndex: 'class',
      render: (classObj) => classObj?.class_name || '-',
    },
    { title: 'Room', dataIndex: 'room_no' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, record) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '15px',
            fontWeight: '600',
            color: record.status === 'Active' ? 'green' : 'red',
            background: record.status === 'Active' ? '#e8ffe8' : '#ffe8e8',
          }}
        >
          {record.status === 'Active' ? 'ON' : 'OFF'}
        </span>
      ),
    },
    {
      title: 'Actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          />
        </Space>
      ),
    },
  ]

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>
        <div className="col-12 col-md-8">
          <div className="header-row">
            <h2 className="page-title">Room Master</h2>
          </div>

          <div className="search-bar">
            <Input
              placeholder="Search room..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />

            <Button
              className="addButton mt-2 mt-md-0"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              style={{ marginLeft: 10 }}
            >
              Add Room
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            bordered
            loading={loading}
            scroll={{ x: 'max-content' }}
            className="shadow border rounded"
          />
        </div>
        <div className="col-12 col-md-2"></div>
      </div>

      <Modal
        title={editingId ? 'Edit Room' : 'Add Room'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            className="addButton"
            type="primary"
            onClick={handleSave}
          >
            Save
          </Button>,
        ]}
      >
        <div className="mb-3">
          <label>Select Class</label>
          <Select
            placeholder="Select class"
            options={classList}
            value={selectedClass}
            onChange={(value) => setSelectedClass(value)}
            style={{ width: '100%' }}
          />
        </div>

        <div className="mb-3">
          <label>Room Name</label>
          <Input
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="Enter room name"
          />
        </div>

        {editingId && (
          <div>
            <label>Room Status</label>
            <br />
            <Switch
              checked={modalStatus}
              loading={updatingStatus}
              onChange={handleModalStatusChange}
              checkedChildren="ON"
              unCheckedChildren="OFF"
              disabled={isRoomBooked}
            />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default RoomMaster
